// P04 Systemeinstellungen (Ausschnitt): E-Mail-Server (SMTP) für Alarme. Umgebungsvariablen haben Vorrang.
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { mk, useSmtpSettings, type SmtpSettings } from '../api/monitoring';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Field, formatDateTime, PageHead, Spinner } from '../components/ui';
import { localeOf } from '../i18n';
import { PlatformSecurityCard } from './account/SecurityPolicy';

interface Form { host: string; port: number; security: 'starttls' | 'tls' | 'none'; username: string; password: string; removePassword: boolean; fromAddress: string }

const formOf = (s: SmtpSettings): Form => ({
  host: s.host ?? '', port: s.port ?? 587, security: s.security ?? 'starttls', username: s.username ?? '', password: '', removePassword: false, fromAddress: s.fromAddress ?? '',
});

export function PlatformSettingsPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const qc = useQueryClient();
  const toast = useToast();
  const { data: s, isLoading } = useSmtpSettings();
  const [f, setF] = useState<Form | null>(null);
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (s) setF(formOf(s));
  }, [s]);
  if (isLoading || !s || !f) return <><PageHead title={t('monitoring.smtp.title')} /><Spinner /></>;
  const env = s.source === 'env';
  const up = <K extends keyof Form>(k: K, v: Form[K]) => setF({ ...f, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/platform/smtp', {
        host: f.host.trim(), port: f.port, security: f.security, username: f.username.trim() || null, fromAddress: f.fromAddress.trim(),
        ...(f.removePassword ? { password: null } : f.password ? { password: f.password } : {}),
      });
      await qc.invalidateQueries({ queryKey: mk.smtp });
      toast.show(t('common.saved'));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    try {
      await api.post('/platform/smtp/test', { to: to.trim() });
      toast.show(t('monitoring.smtp.testSent', { to: to.trim() }));
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <>
      <PageHead title={t('monitoring.smtp.title')} sub={t('monitoring.smtp.sub')} />
      <div className="col" style={{ maxWidth: 680, gap: 16 }}>
        {env && <div className="hint" role="status">{t('monitoring.smtp.envNote')}</div>}
        {s.source === 'none' && <div className="hint warn" role="status">{t('monitoring.smtp.notConfigured')}</div>}
        <div className="card col">
          <h2 style={{ margin: 0 }}>{t('monitoring.smtp.server')}</h2>
          <div className="grid-2">
            <Field label={t('monitoring.smtp.host')}><input disabled={env} value={f.host} onChange={(e) => up('host', e.target.value)} placeholder="mail.firma.ch" /></Field>
            <Field label={t('monitoring.smtp.port')}><input disabled={env} type="number" min={1} max={65535} value={f.port} onChange={(e) => up('port', Number(e.target.value))} /></Field>
          </div>
          <Field label={t('monitoring.smtp.security')}>
            <select disabled={env} value={f.security} onChange={(e) => {
              const sec = e.target.value as Form['security'];
              setF({ ...f, security: sec, port: sec === 'tls' && f.port === 587 ? 465 : sec === 'starttls' && f.port === 465 ? 587 : f.port });
            }}>
              {(['starttls', 'tls', 'none'] as const).map((k) => <option key={k} value={k}>{t(`monitoring.smtp.sec.${k}`)}</option>)}
            </select>
          </Field>
          <div className="grid-2">
            <Field label={t('monitoring.smtp.username')}><input disabled={env} value={f.username} onChange={(e) => up('username', e.target.value)} autoComplete="off" /></Field>
            <Field label={t('monitoring.smtp.password')} hint={s.hasPassword ? t('monitoring.smtp.passwordKeep') : undefined}>
              <input disabled={env || f.removePassword || !s.canStorePassword} type="password" value={f.password} onChange={(e) => up('password', e.target.value)} autoComplete="new-password"
                placeholder={s.hasPassword ? '••••••••' : ''} />
            </Field>
          </div>
          {!env && s.hasPassword && <label className="check"><input type="checkbox" checked={f.removePassword} onChange={(e) => up('removePassword', e.target.checked)} />{t('monitoring.smtp.removePassword')}</label>}
          {!env && !s.canStorePassword && <div className="hint warn">{t('monitoring.smtp.noSecret')}</div>}
          <Field label={t('monitoring.smtp.from')} hint={t('monitoring.smtp.fromHint')}><input disabled={env} value={f.fromAddress} onChange={(e) => up('fromAddress', e.target.value)} /></Field>
          {s.updatedAt && <div className="small muted">{t('monitoring.smtp.updated', { when: formatDateTime(s.updatedAt, locale) })}</div>}
          {!env && <div className="row"><button className="btn primary" disabled={busy || !f.host.trim() || !f.fromAddress.trim()} onClick={() => void save()}><Icon name="check" />{t('common.save')}</button></div>}
        </div>
        <div className="card col">
          <h2 style={{ margin: 0 }}>{t('monitoring.smtp.test')}</h2>
          <div className="row">
            <input className="grow" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="ich@firma.ch" aria-label={t('monitoring.smtp.testTo')} />
            <button className="btn" disabled={s.source === 'none' || !/.+@.+\..+/.test(to)} onClick={() => void test()}><Icon name="send" />{t('monitoring.smtp.testSend')}</button>
          </div>
        </div>
        <PlatformSecurityCard />
      </div>
    </>
  );
}
